/**
 * Теги сегментации аудитории для Sentry-событий (транзакции и checkout-события).
 *
 * webview        — чекаут открыт внутри нативного приложения. Флаг WEBVIEW
 *                  приходит в ответе GET /init и хранится в сторе (isWebview):
 *                  надёжнее, чем User-Agent, который для iOS-WebView неотличим
 *                  от обычного Safari.
 * vpn_banner     — на хост-странице отрендерен серверный баннер «вы используете
 *                  VPN» (.vpn_message__container). Факт VPN вычисляет Битрикс
 *                  (по IP), Vue-приложение просто читает наличие блока в DOM.
 * net_*          — оценка канала пользователя из navigator.connection
 *                  (effectiveType: '4g' | '3g' | ...; downlink, Мбит/с; rtt, мс;
 *                  saveData — режим экономии трафика). Есть в Chromium и Android
 *                  WebView; в Safari/Firefox свойства отсутствуют — теги просто
 *                  не ставятся.
 * device_memory  — navigator.deviceMemory (ГБ), если браузер его отдаёт.
 * cpu_cores      — navigator.hardwareConcurrency, если браузер его отдаёт.
 *
 * ВАЖНО: getAudienceTags вызывается из beforeSendTransaction — в момент
 * ЗАКРЫТИЯ транзакции (T+20..30 с от загрузки). navigator.connection —
 * скользящие оценки, которые циркулярно обновляются в течение сессии
 * (медленные ответы API сами ухудшают rtt/downlink), поэтому к моменту
 * разметки они описывают уже не условия загрузки, а «нагрузку» сессии.
 * Поэтому сеть и мощность устройства фиксируются ОДИН РАЗ на бутстрапе
 * (captureAudienceSnapshot() из setupSentry, до Sentry.init), а разметка
 * берёт их из снимка. webview и vpn_banner остаются live-чтением: webview
 * наполняется только после ответа /init (снапшот на бутстрапе дал бы ложный
 * false), vpn_banner статичен, но live-чтение не вредит. Если снимок не
 * снят (тесты/прямой вызов) — fallback на live-чтение как раньше.
 */

const VPN_BANNER_SELECTOR = '.vpn_message__container'

/**
 * Снимок окружения, снятый на бутстрапе (или null, пока не снят).
 * Значения уже приведены к формату тегов (строки) — подстановкой в событие
 * занимается getAudienceTags.
 */
let audienceSnapshot = null

/**
 * Одноразовый снимок сети и мощности устройства на бутстрапе.
 * Повторные вызовы — no-op: возвращается уже снятый снимок.
 *
 * @param {{ doc?: Document, nav?: Navigator }} [params] — инъекция для тестов.
 *        `doc` принят для симметрии с getAudienceTags: DOM-сигналы
 *        (vpn_banner) в снимок не входят, они читаются live в момент разметки.
 * @returns {Object} снимок (теги в финальном строковом формате).
 */
// eslint-disable-next-line no-unused-vars -- doc принят для симметрии сигнатуры с getAudienceTags
export function captureAudienceSnapshot({ doc, nav } = {}) {
  if (audienceSnapshot) return audienceSnapshot

  const navigator_ =
    nav ?? (typeof navigator !== 'undefined' ? navigator : undefined)
  const conn =
    navigator_?.connection ??
    navigator_?.mozConnection ??
    navigator_?.webkitConnection

  audienceSnapshot = {
    ...(conn?.effectiveType ? { net_effective: conn.effectiveType } : {}),
    ...(Number.isFinite(conn?.downlink)
      ? { net_downlink: String(conn.downlink) }
      : {}),
    ...(Number.isFinite(conn?.rtt) ? { net_rtt: String(conn.rtt) } : {}),
    ...(typeof conn?.saveData === 'boolean'
      ? { net_save_data: String(conn.saveData) }
      : {}),
    ...(Number.isFinite(navigator_?.deviceMemory)
      ? { device_memory: String(navigator_.deviceMemory) }
      : {}),
    ...(Number.isFinite(navigator_?.hardwareConcurrency)
      ? { cpu_cores: String(navigator_.hardwareConcurrency) }
      : {}),
  }

  return audienceSnapshot
}

/** Сброс снимка — только для тестов (изоляция модульного состояния). */
export function __resetAudienceSnapshotForTests() {
  audienceSnapshot = null
}

/**
 * Теги аудитории для Sentry-события. webview/vpn_banner читаются live;
 * net_* и device-теги — из бутстрап-снимка, а без снимка — live (fallback).
 */
export function getAudienceTags({ store, doc, nav } = {}) {
  const document_ =
    doc ?? (typeof document !== 'undefined' ? document : undefined)
  const navigator_ =
    nav ?? (typeof navigator !== 'undefined' ? navigator : undefined)

  const conn =
    navigator_?.connection ??
    navigator_?.mozConnection ??
    navigator_?.webkitConnection

  const environmentTags = audienceSnapshot ?? {
    ...(conn?.effectiveType ? { net_effective: conn.effectiveType } : {}),
    ...(Number.isFinite(conn?.downlink)
      ? { net_downlink: String(conn.downlink) }
      : {}),
    ...(Number.isFinite(conn?.rtt) ? { net_rtt: String(conn.rtt) } : {}),
  }

  return {
    webview: String(!!store?.state?.isWebview),
    vpn_banner: String(
      !!document_ && document_.querySelector(VPN_BANNER_SELECTOR) !== null
    ),
    ...environmentTags,
  }
}
